import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@reach/router';
import AppButton from 'components/AppButton';
import { getPage } from 'utils/pages';
import SectionHeading from 'components/SectionHeading';
import SectionSubtitle from 'components/SectionSubtitle';
import stepIcon1 from './RepairProcces/step1.png';
import stepIcon2 from './RepairProcces/step2.png';
import stepIcon3 from './RepairProcces/step3.svg';
import stepIcon4 from './RepairProcces/step4.svg';
import stepIcon5 from './RepairProcces/step5.png';
import stepIcon6 from './RepairProcces/step6.svg';
import stepIcon7 from './RepairProcces/step7.svg';
import titleUnderline from '../OnlineRepairs/0707.png';
import decorLogo from './Kopie von RR-LOGO 1.png';
import styles from './HighlightsSlider.module.css';


const STEPS = [
  {
    number: 1,
    icon: stepIcon1,
    titleKey: 'Highlights step 1 title',
    descriptionKey: 'Highlights step 1 description',
  },
  {
    number: 2,
    icon: stepIcon2,
    titleKey: 'Highlights step 2 title',
    descriptionKey: 'Highlights step 2 description',
  },
  {
    number: 3,
    icon: stepIcon3,
    titleKey: 'Highlights step 3 title',
    descriptionKey: 'Highlights step 3 description',
  },
  {
    number: 4,
    icon: stepIcon4,
    titleKey: 'Highlights step 4 title',
    descriptionKey: 'Highlights step 4 description',
  },
  {
    number: 5,
    icon: stepIcon5,
    titleKey: 'Highlights step 5 title',
    descriptionKey: 'Highlights step 5 description',
  },
  {
    number: 6,
    icon: stepIcon6,
    titleKey: 'Highlights step 6 title',
    descriptionKey: 'Highlights step 6 description',
  },
  {
    number: 7,
    icon: stepIcon7,
    titleKey: 'Highlights step 7 title',
    descriptionKey: 'Highlights step 7 description',
  },
] as const;
const CTA_REVEAL_THRESHOLD_DESKTOP = 0.91;
const CTA_REVEAL_THRESHOLD_MOBILE = 0.97;

type StepListStyle = React.CSSProperties & {
  '--card-height'?: string;
};

export default function HighlightsSlider() {
  const { t } = useTranslation();
  const stageRef = useRef<HTMLDivElement | null>(null);
  const stepRefs = useRef<Array<HTMLElement | null>>([]);
  const rafRef = useRef<number | null>(null);
  const progressRef = useRef(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [maxCardHeight, setMaxCardHeight] = useState(0);

  useEffect(() => {
    function clamp(value: number, min: number, max: number) {
      return Math.max(min, Math.min(max, value));
    }

    function updateProgress() {
      const stage = stageRef.current;
      if (!stage) {
        rafRef.current = null;
        return;
      }

      const viewportHeight = window.innerHeight || 1;
      const viewportWidth = window.innerWidth || 1024;
      const isMobile = viewportWidth <= 767;
      const rect = stage.getBoundingClientRect();
      // Start folding when stage top reaches 50% of viewport height.
      const trackStart = viewportHeight * 0.5;
      const trackLength = rect.height + viewportHeight * (isMobile ? 0.1 : 0.16);
      const nextProgress = clamp((trackStart - rect.top) / trackLength, 0, 1);

      if (Math.abs(nextProgress - progressRef.current) > 0.0025) {
        progressRef.current = nextProgress;
        setScrollProgress(nextProgress);
      }

      rafRef.current = null;
    }

    function requestUpdate() {
      if (rafRef.current !== null) {
        return;
      }
      rafRef.current = window.requestAnimationFrame(updateProgress);
    }

    requestUpdate();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    function measureCards() {
      const nextMax = stepRefs.current.reduce((maxHeight, element) => {
        if (!element) {
          return maxHeight;
        }
        return Math.max(maxHeight, element.getBoundingClientRect().height);
      }, 0);

      if (nextMax > 0) {
        const rounded = Math.ceil(nextMax);
        setMaxCardHeight((current) => (Math.abs(current - rounded) > 1 ? rounded : current));
      }
    }

    const frameId = window.requestAnimationFrame(measureCards);
    window.addEventListener('resize', measureCards);

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measureCards);
      stepRefs.current.forEach((element) => {
        if (element) {
          observer?.observe(element);
        }
      });
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', measureCards);
      observer?.disconnect();
    };
  }, [t]);

  const stageStyle = useMemo(
    () => {
      if (maxCardHeight <= 0) {
        return undefined;
      }

      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
      const isDesktop = viewportWidth >= 1024;
      const isTablet = viewportWidth >= 768 && viewportWidth < 1024;

      // Keep enough room for stacking but avoid a large empty gap before CTA.
      const foldFactor = isDesktop ? 0.5 : isTablet ? 0.47 : 0.43;
      const holdFactor = isDesktop ? 0.055 : isTablet ? 0.05 : 0.045;
      const releaseFactor = isDesktop ? 0.065 : isTablet ? 0.058 : 0.052;

      const foldTravel = (STEPS.length - 1) * maxCardHeight * foldFactor;
      const holdAndRelease = maxCardHeight * holdFactor;
      const releaseBuffer = maxCardHeight * releaseFactor;
      const minHeight = Math.round(maxCardHeight + foldTravel + holdAndRelease + releaseBuffer);

      return { minHeight: `${minHeight}px` };
    },
    [maxCardHeight]
  );

  const stepsStyle = useMemo<StepListStyle>(
    () => ({
      '--card-height': maxCardHeight > 0 ? `${maxCardHeight}px` : 'auto',
    }),
    [maxCardHeight]
  );

  const stepStyles = useMemo(
    () =>
      STEPS.map((step, stepIndex) => {
        if (stepIndex === 0) {
          return {
            ['--fold-progress' as '--fold-progress']: 0,
            ['--stack-index' as '--stack-index']: 0,
            ['--card-shadow' as '--card-shadow']: 0,
            zIndex: 1,
          };
        }

        const stackIndex = stepIndex;
        // Soften index growth to keep adjacent gaps more uniform.
        const normalizedStackIndex = Math.pow(stackIndex, 0.78);
        const sequenceIndex = stepIndex - 1;
        // Uniform per-card timing: each next step starts at equal scroll intervals.
        const stageStart = 0.03 + sequenceIndex * 0.09;
        const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
        const isMobile = viewportWidth <= 767;
        const stageDuration = isMobile ? 0.78 : 0.72;
        const rawProgress = (scrollProgress - stageStart) / stageDuration;
        const gain = isMobile ? 1.06 : 1.08;
        const localProgress = Math.max(0, Math.min(1, rawProgress * gain));
        const eased = 1 - (1 - localProgress) ** 3;
        const revealThreshold = isMobile ? CTA_REVEAL_THRESHOLD_MOBILE : CTA_REVEAL_THRESHOLD_DESKTOP;
        const effectiveProgress = sequenceIndex === STEPS.length - 2
          ? Math.min(eased, revealThreshold)
          : eased;

        return {
          ['--fold-progress' as '--fold-progress']: Number(effectiveProgress.toFixed(4)),
          ['--stack-index' as '--stack-index']: Number(normalizedStackIndex.toFixed(4)),
          ['--card-shadow' as '--card-shadow']: Number((effectiveProgress * 0.55).toFixed(4)),
          zIndex: 10 + stackIndex,
        };
      }),
    [scrollProgress]
  );

  const shouldShowCta = useMemo(() => {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const isMobile = viewportWidth <= 767;
    const sequenceIndex = STEPS.length - 2; // step 7
    const stageStart = 0.03 + sequenceIndex * 0.09;
    const stageDuration = isMobile ? 0.78 : 0.72;
    const rawProgress = (scrollProgress - stageStart) / stageDuration;
    const gain = isMobile ? 1.06 : 1.08;
    const localProgress = Math.max(0, Math.min(1, rawProgress * gain));
    const eased = 1 - (1 - localProgress) ** 3;

    const revealThreshold = isMobile ? CTA_REVEAL_THRESHOLD_MOBILE : CTA_REVEAL_THRESHOLD_DESKTOP;
    return eased >= revealThreshold;
  }, [scrollProgress]);

  return (
    <section className={styles.section} aria-labelledby="highlights-heading">
      <img src={decorLogo} alt="" className={styles.decorImage} aria-hidden />
      <div className={styles.container}>
        <div className={styles.headerAnchor}>
          <header className={styles.header}>
            <div className={styles.titleWrapper}>
              <SectionHeading id="highlights-heading" size="large" className={styles.title}>
                {t('Highlights title')}
              </SectionHeading>
              <img src={titleUnderline} alt="" className={styles.titleUnderline} aria-hidden />
            </div>
            <SectionSubtitle size="medium" className={styles.subtitle}>{t('Highlights subtitle')}</SectionSubtitle>
          </header>
        </div>

        <div ref={stageRef} className={styles.stepsStage} style={stageStyle}>
          <div className={styles.stepsSticky}>
            <div className={styles.steps} style={stepsStyle}>
              {STEPS.map((step, stepIndex) => (
                <article
                  key={step.number}
                  className={styles.step}
                  style={stepStyles[stepIndex]}
                  ref={(element) => {
                    stepRefs.current[stepIndex] = element;
                  }}
                >
                  <div>
                    <div className={styles.stepIcon}>
                      <img src={step.icon} alt="" aria-hidden="true" />
                    </div>
                    <div className={styles.stepNumberBadge} aria-hidden="true">
                      <span className={styles.stepNumber}>{step.number}</span>
                    </div>
                  </div>
                
                  <div className={styles.stepContent}>
                    <h3 className={styles.stepTitle}>{t(step.titleKey)}</h3>
                    <p className={styles.stepDescription}>{t(step.descriptionKey)}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
        <div className={`${styles.ctaWrapper} ${shouldShowCta ? styles.ctaVisible : ''}`}>
          <AppButton color="secondary" component={Link} to={`${getPage('product')}${getPage('select')}`} variant="contained">
            {t('Highlights cta label')}
          </AppButton>
        </div>
      </div>
    </section>
  );
}
